
import React from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Filter, Download, Settings, ChevronDown } from 'lucide-react';

interface AffiliateUsageLimitsToolbarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  selectedFilter: string;
  onFilterChange: (filter: string) => void;
  selectedCount: number;
  totalCount: number;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onBulkAction: (action: string) => void;
  isAllSelected: boolean;
}

export const AffiliateUsageLimitsToolbar = ({
  searchTerm,
  onSearchChange,
  selectedFilter,
  onFilterChange,
  selectedCount,
  totalCount,
  onSelectAll,
  onDeselectAll,
  onBulkAction,
  isAllSelected
}: AffiliateUsageLimitsToolbarProps) => {
  const filters = [
    { key: 'all', label: 'All Affiliates', count: totalCount },
    { key: 'high', label: 'High Usage (>80%)', count: 0 },
    { key: 'medium', label: 'Medium Usage (60-80%)', count: 0 },
    { key: 'low', label: 'Low Usage (<60%)', count: 0 }
  ];

  const currentFilter = filters.find(f => f.key === selectedFilter) || filters[0];

  return (
    <div className="space-y-4">
      {/* Search and Filter Row */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        {/* Search - More prominent */}
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search affiliates by name or email..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9 w-full"
          />
        </div>

        {/* Filter Dropdown - Compact */}
        <div className="flex-shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="min-w-[200px] justify-between">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4" />
                  {currentFilter.label}
                  {currentFilter.count > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {currentFilter.count}
                    </Badge>
                  )}
                </div>
                <ChevronDown className="h-4 w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[200px]">
              {filters.map((filter) => (
                <DropdownMenuItem
                  key={filter.key}
                  onClick={() => onFilterChange(filter.key)}
                  className={selectedFilter === filter.key ? "bg-accent" : ""}
                >
                  <div className="flex items-center justify-between w-full">
                    <span>{filter.label}</span>
                    {filter.count > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {filter.count}
                      </Badge>
                    )}
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Bulk Actions Row */}
      {selectedCount > 0 && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 bg-muted/50 rounded-lg border">
          <div className="flex items-center gap-3">
            <Checkbox
              checked={isAllSelected}
              onCheckedChange={isAllSelected ? onDeselectAll : onSelectAll}
            />
            <span className="text-sm font-medium">
              {selectedCount} of {totalCount} selected
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={onDeselectAll}
            >
              Clear Selection
            </Button>
          </div>

          <div className="flex gap-2 flex-wrap">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings className="h-4 w-4 mr-2" />
                  Bulk Actions
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onBulkAction('setDailyLimits')}>
                  Set Daily Limits
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onBulkAction('setMonthlyLimits')}>
                  Set Monthly Limits
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onBulkAction('resetToDefaults')}>
                  Reset to Defaults
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onBulkAction('export')}>
                  <Download className="h-4 w-4 mr-2" />
                  Export Selected
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}
    </div>
  );
};
